/**
 * FE-side fallback for the BE rule engine (BE issue #43, not yet implemented).
 *
 * When `GET /api/recommendations` returns `[]` (because the BE rule engine is
 * still an empty stub), we compute a short list of recommendations locally so
 * the demo always has cards to show. As soon as the BE rule engine ships,
 * `useRecommendations()` will return non-empty data and this fallback is
 * bypassed (see `useRecommendations()` in `frontend/src/hooks/queries.ts`).
 *
 * Rules implemented here are a strict subset of the canonical wiki page
 * [[Recommendations-Rules]]. We keep the catalogue small + high-leverage so
 * the demo cards say something credible:
 *
 *   R-001  high   missing `liability_personal` if any child member exists.
 *   R-005  medium missing `property_home` content insurance if any
 *                  `property_building` contract exists.
 *   R-002  high   missing `life` for any `adult_earner` member.
 *   R-006  medium missing `property_building` when no property contract at all.
 *   R-007  medium missing `vehicle_mtpl` (legally required) — always fires if
 *                  the user has no vehicle contract yet.
 *   R-008  low    missing `travel` if no travel contract.
 *
 * The output is **deduplicated** by `rec.id` and **sorted by severity** so the
 * top of the stack is always the most urgent. We cap at 4 cards by default to
 * keep the bottom strip from spilling off-screen on a 1366px viewport.
 */
import type {
  AreaCode,
  Contract,
  Member,
  Recommendation,
  RecommendationSeverity,
} from '@shared/schemas'

const SEVERITY_RANK: Record<RecommendationSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
}

export type DeriveRecsInput = {
  contracts: readonly Contract[]
  members: readonly Member[]
}

/**
 * Compute a short list of recommendations from the user's current contracts
 * and family members. Stable + idempotent — called inside a React Query
 * `select` so it must be cheap. No I/O, no random ids.
 */
export function deriveRecommendations(
  { contracts, members }: DeriveRecsInput,
  limit = 4,
): Recommendation[] {
  const types = new Set<AreaCode>(contracts.map((c) => c.type))
  const hasType = (t: AreaCode): boolean => types.has(t)

  const memberIds = members.map((m) => m.id)
  const hasChild = members.some((m) => m.role === 'child')
  const earners = members.filter((m) => m.role === 'adult_earner')

  const out: Recommendation[] = []

  // R-001 — Personal liability when there's a child. High severity because a
  // child's accidental harm to a third party is the single most-expensive
  // uncovered risk for Czech families per the wiki Domain-Glossary.
  if (hasChild && !hasType('liability_personal')) {
    out.push({
      id: 'derived:R-001',
      rule_id: 'R-001',
      severity: 'high',
      kind: 'missing_area',
      title: 'Odpovědnost za škodu',
      message:
        'V rodině jsou děti, ale chybí pojištění občanské odpovědnosti. Pokrývá škody, které způsobí třetí osobě.',
      area: 'liability_personal',
      affects: memberIds,
    })
  }

  // R-002 — Life insurance for the earner(s). Each earner gets one rec.
  for (const earner of earners) {
    if (!hasType('life')) {
      out.push({
        id: `derived:R-002:${earner.id}`,
        rule_id: 'R-002',
        severity: 'high',
        kind: 'missing_area',
        title: 'Životní pojištění',
        message: `${earner.name} je živitel rodiny bez životního pojištění. Pokud by vypadl(a) z příjmu, krylo by to nejhorší dopad.`,
        area: 'life',
        affects: [earner.id],
      })
      // Only emit once even with multiple earners — keeps the stack readable.
      break
    }
  }

  // R-005 — Home contents when there's a building. Burglary + fire protection
  // for the stuff inside, the building policy alone doesn't cover this.
  if (hasType('property_building') && !hasType('property_home')) {
    out.push({
      id: 'derived:R-005',
      rule_id: 'R-005',
      severity: 'medium',
      kind: 'missing_area',
      title: 'Pojištění domácnosti',
      message:
        'Nemovitost je pojištěná, ale vybavení uvnitř ne. Doplň pojištění domácnosti pro nábytek, elektroniku a osobní věci.',
      area: 'property_home',
      affects: memberIds,
    })
  }

  // R-006 — Missing property entirely. Lower priority than the targeted
  // contents-on-building rule above (different rule_id by design).
  if (!hasType('property_building') && !hasType('property_home')) {
    out.push({
      id: 'derived:R-006',
      rule_id: 'R-006',
      severity: 'medium',
      kind: 'missing_area',
      title: 'Pojištění nemovitosti',
      message:
        'Bez pojištění nemovitosti i menší škoda (vytopení, požár) může znamenat statisíce z vlastní kapsy.',
      area: 'property_building',
      affects: memberIds,
    })
  }

  // R-007 — MTPL is **legally required** in CZ. We surface it as `high` if no
  // vehicle contract exists. (If the user genuinely has no car, they'll just
  // dismiss the card; that's a deliberate choice — false-positive cheaper
  // than missing a legal-compliance gap.)
  if (!hasType('vehicle_mtpl') && !hasType('vehicle_casco')) {
    out.push({
      id: 'derived:R-007',
      rule_id: 'R-007',
      severity: 'high',
      kind: 'missing_area',
      title: 'Povinné ručení',
      message:
        'Povinné ručení je v ČR ze zákona povinné pro každý provozovaný vůz.',
      area: 'vehicle_mtpl',
      affects: memberIds,
    })
  }

  // R-008 — Travel. Low severity, but useful filler before holidays.
  if (!hasType('travel')) {
    out.push({
      id: 'derived:R-008',
      rule_id: 'R-008',
      severity: 'low',
      kind: 'missing_area',
      title: 'Cestovní pojištění',
      message:
        'Bez cestovního pojištění může neplánovaný úraz nebo zrušení letu v zahraničí vyjít na desetitisíce.',
      area: 'travel',
      affects: memberIds,
    })
  }

  // Stable sort by severity → high cards first. Within the same severity we
  // preserve insertion order so the rule list above acts as a tiebreaker.
  out.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
  return out.slice(0, limit)
}
